import type { TFunction } from "react-i18next";
import { z } from "zod";
import type { Api } from "../../services/api";
import { JOURNAL_FIELD_NAMES } from "./journalFieldNames";

/**
 * How a journal's SIGAPS band is spoken, everywhere Juisci states one.
 *
 * The band itself is an administrative letter grade; what a reader sees is the
 * letter, the quartile it maps to, and the percentile phrase. Owned here rather
 * than by either surface: the article page's evidence sheet and JuisciGPT's
 * source sheet state the same rank, and a rank that reads differently depending
 * on where it is shown is the bug this prevents.
 *
 * Only the VOCABULARY lives here. The two surfaces draw the mark differently on
 * purpose (see `JournalBandBadge`), and their layouts are separate designs.
 */
type ArticleDetail =
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];
export type JournalRank = NonNullable<ArticleDetail["journalRank"]>;
type JournalFieldBand = NonNullable<JournalRank["fieldBand"]>;
export type JournalBand = JournalFieldBand["band"];
export type JournalFieldUi = JournalFieldBand["field"];

// The SIGAPS band, displayed as a bare rank letter (the band name itself is
// French administrative vocabulary the product does not surface).
export const BAND_LABEL = {
  a_plus: "A+",
  a: "A",
  b: "B",
  c: "C",
  d: "D",
  e: "E",
} as const satisfies Record<JournalBand, string>;

// The band's IF-percentile range, spoken as the quartile clinicians already
// know from the journal rankings: a_plus/a/b sit in the top 25% (Q1), c = 25–50% (Q2),
// d = 50–75% (Q3), e = bottom 25% (Q4). Untranslated — Q1..Q4 is universal.
export const BAND_QUARTILE = {
  a_plus: "Q1",
  a: "Q1",
  b: "Q1",
  c: "Q2",
  d: "Q3",
  e: "Q4",
} as const satisfies Record<JournalBand, string>;

/** The indexes that positively vouch for the journal, in badge order. */
export function indexedIn(rank: JournalRank): string[] {
  return [...(rank.medline ? ["Medline"] : []), ...(rank.doaj ? ["DOAJ"] : [])];
}

// Read at a boundary, not trusted from the type: after a rollback an older
// API sends no `fieldBand` (it sent `band` and `bands`), and a new bundle must
// read that as no band rather than crash on `fieldBand.band`.
const RANK_BANDS = [
  "a_plus",
  "a",
  "b",
  "c",
  "d",
  "e",
] as const satisfies readonly JournalBand[];
const withFieldBand = z.object({
  fieldBand: z
    .object({ band: z.enum(RANK_BANDS), field: z.string() })
    .nullable(),
});
const FIELD_NAMES: ReadonlyMap<string, (t: TFunction) => string> = new Map(
  Object.entries(JOURNAL_FIELD_NAMES),
);

/**
 * The band the reader sees for a journal, with the name of the research field
 * it is a rank in, in the reader's language. The api picks the band (one per
 * journal, `JOURNAL_FIELD_LABEL_RULE` in domain/journal/field-bands.ts), so
 * every surface states the same rank for the same journal. Null when the
 * journal has no banded field, and for a body that has no `fieldBand` at all.
 */
export function fieldBand(
  t: TFunction,
  rank: object,
): { band: JournalBand; field: string } | null {
  const parsed = withFieldBand.safeParse(rank);
  if (!parsed.success || parsed.data.fieldBand === null) return null;
  const name = FIELD_NAMES.get(parsed.data.fieldBand.field);
  if (name === undefined) return null;
  return { band: parsed.data.fieldBand.band, field: name(t) };
}

/**
 * SIGN-OFF: the wording of each band, from the banding rule in
 * domain/journal/field-bands.ts (a = top 10% by IF within the field,
 * b = 10–25%, c = 25–50%, d = 50–75%, e = bottom 25%; a_plus = the fixed
 * flagship list). The caller supplies the field name from `fieldBand`.
 */
export function bandPhrase(
  t: TFunction,
  band: JournalBand,
  field: string,
): string {
  if (band === "a_plus") return t("Flagship journal");
  switch (band) {
    case "a":
      return t("Top 10% in {{field}}", { field });
    case "b":
      return t("Top 25% in {{field}}", { field });
    case "c":
      return t("Top 50% in {{field}}", { field });
    case "d":
      return t("Top 75% in {{field}}", { field });
    case "e":
      return t("Bottom 25% in {{field}}", { field });
    default:
      throw new Error(band satisfies never);
  }
}
